import dayjs from 'dayjs';
import { NO_VALUE } from '@/app/consts/NoValue';
import { ColumnType } from '@/app/enums/ColumnType';
import { formatDateToString } from '@/app/helpers/formatDateToString';
import { type HeaderEntryDto } from '@/app/models/HeaderEntryDto';
import { getRowValue } from '../getRowValue';
import { type BackendExport, type ExportFieldDef, type ExportFormat } from '../types';

/** Acima disso a exportação vai para segundo plano em vez de esperar no modal. */
export const BACKGROUND_EXPORT_THRESHOLD = 1_000;

/** Nome do arquivo na prévia quando a tela não tem título, e o nome com que a tarefa nasce (igual ao backend). */
export const DEFAULT_EXPORT_TITLE = 'Exportação';

/** "1 linha", "12.480 linhas". */
export const countLabel = (count: number, singular: string, plural: string) =>
  `${count.toLocaleString('pt-BR')} ${count === 1 ? singular : plural}`;

/** Teto de `ids` do contrato em "Selecionados". */
export const MAX_SELECTED_ROWS = 1_000;

/**
 * Se a exportação pelo backend oferece Página atual e Selecionados, que mandam `ids`. Relatório agregado não
 * tem `id` na linha, e o exportador dele só exporta o filtro inteiro. Página vazia não prova nada; seleção prova.
 */
export const exportsByRow = ({ selectedIds }: BackendExport, pageRows: Record<string, unknown>[]) =>
  selectedIds.length > 0 || (pageRows.length > 0 && pageRows.every((row) => row.id != null));

/** Tetos de linhas por formato, espelhados do backend (que continua sendo a verdade) para avisar antes de enviar. */
const ROW_LIMITS: Partial<Record<ExportFormat, number>> = { pdf: 5_000, xlsx: 100_000 };

export const exportRowLimit = (format: ExportFormat) => ROW_LIMITS[format];

const formatNumber = (value: unknown, fractionDigits?: number) =>
  Number(value).toLocaleString(
    'pt-BR',
    fractionDigits === undefined
      ? { maximumFractionDigits: 20 }
      : { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits },
  );

/**
 * Valor de uma célula da prévia. A chave do catálogo é um caminho no JSON do `/list` (`category.name`) e o
 * `type` diz como o backend vai desenhar o valor (mesmos formatos do `ExportFormats` de lá). É prévia: o
 * arquivo é do backend. Campo de tabela em memória não tem `type` e usa o próprio `format`.
 */
export function formatPreviewValue(row: Record<string, unknown>, field: ExportFieldDef) {
  if (field.format) return field.format(row) || NO_VALUE;

  const value: unknown = getRowValue(row, field.key);
  if (value === undefined || value === null || value === '') return NO_VALUE;

  switch (field.type) {
    case ColumnType.MONEY:
      return formatNumber(value, 2);
    case ColumnType.QUANTITY:
      return formatNumber(value);
    case ColumnType.INTEGER:
      return formatNumber(value, 0);
    case ColumnType.PERCENTAGE:
      return `${formatNumber(value, 2)}%`;
    case ColumnType.DATE:
      return dayjs(value as string).format('DD/MM/YYYY');
    case ColumnType.DATE_TIME:
      return dayjs(value as string).format('DD/MM/YYYY HH:mm');
    case ColumnType.BOOLEAN:
      return value === true ? 'Sim' : 'Não';
  }
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  // ENUM chega como `{ value, description }`; qualquer outro objeto não tem o que mostrar.
  return (value as { description?: string }).description ?? NO_VALUE;
}

export function normalizeExportLabel(label: string): string {
  return label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLocaleLowerCase('pt-BR');
}

/**
 * Texto de um valor do filtro no cabeçalho: o nome guardado (`'Bebidas'`), a descrição de um enum
 * (`{ value: 'BILLED', description: 'Faturado' }`) ou a lista de uma multisseleção, unida por `, `.
 * Vazio vira `undefined`, e o filtro não aparece.
 */
function describeFilterValue(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined;
  if (Array.isArray(value)) return value.map(describeFilterValue).filter(Boolean).join(', ') || undefined;
  if (value && typeof value === 'object' && 'description' in value) return describeFilterValue(value.description);
  return undefined;
}

function describePeriod(start: unknown, end: unknown): string | undefined {
  const from = typeof start === 'string' && start ? formatDateToString(start) : undefined;
  const to = typeof end === 'string' && end ? formatDateToString(end) : undefined;
  if (from && to) return `${from} a ${to}`;
  if (from) return `A partir de ${from}`;
  if (to) return `Até ${to}`;
  return undefined;
}

/**
 * Resumo dos filtros para o cabeçalho do arquivo exportado, com os nomes que a tela mostra
 * (`Categoria: Bebidas`), não com ids. Abre com o período (`start_date`/`end_date`), que nenhuma
 * tela precisa declarar. `labels` liga o campo do filtro que carrega o nome escolhido
 * (`category_label` ao lado do `category_id`, ou o próprio enum `order_status`) ao nome do campo
 * na tela, na ordem em que a tela quer ver.
 */
export function buildExportFilters(
  filter: object | undefined,
  labels: Record<string, string>,
  search: string | undefined,
): HeaderEntryDto[] {
  const values: Record<string, unknown> = { ...filter };
  const entries: HeaderEntryDto[] = [];
  const period = describePeriod(values.start_date, values.end_date);
  if (period) {
    entries.push({ label: 'Período', value: period });
  }
  const term = search?.trim();
  if (term) {
    entries.push({ label: 'Busca', value: term });
  }
  for (const [labelKey, label] of Object.entries(labels)) {
    const value = describeFilterValue(values[labelKey]);
    if (value) {
      entries.push({ label, value });
    }
  }
  return entries;
}

export function buildFilename(
  pageTitle: string | undefined,
  pageRange: string | undefined,
  format: ExportFormat,
): string {
  const parts: string[] = [];
  if (pageTitle) {
    parts.push(pageTitle.toLowerCase().replace(/\s+/g, '-'));
  } else {
    parts.push('export');
  }
  if (pageRange) {
    parts.push(pageRange);
  }
  const now = new Date();
  const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
  parts.push(timestamp);
  const ext = format === 'xlsx' ? 'xlsx' : format;
  return `${parts.join('_')}.${ext}`;
}
